import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  params,
  shape,
  string as stringType,
  fragment,
  customType,
  arrayOf,
  useDataModel,
} from '@thd-nucleus/data-sources';
import {
  Card,
  CardMedia,
  CardTitle,
  CardContent
} from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';

import { RichText } from './RichText';
import './capability_card.scss';

const CapabilityCard = ({
  componentId, componentClass, isHorizontal, headline, richText, componentPosition
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('capability_card.ready'); }, []);

  const { data } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    skip: (!!headline || !!richText),
  });

  const headlineData = headline ?? data?.component?.headline;
  const hasImage = !!data?.component?.previewImage;
  const hasIcon = !!data?.component?.previewIcon;

  let richTextData;

  try {
    richTextData = JSON.parse(richText ?? data?.component?.richTextContent);
    // eslint-disable-next-line no-empty
  } catch (error) { }

  let imageUrl;

  if (hasImage) {
    const damDownloadedContentImage = data?.component?.previewImage?.damDownloadedContent;
    const damContentSelectorImage = data?.component?.previewImage?.damContentSelector?.assetData?.[0];

    imageUrl = damDownloadedContentImage?.url || damContentSelectorImage?.selectedImageUrl || imageUrl;
  }

  let iconUrl;

  if (hasIcon) {
    const damDownloadedContentImage = data?.component?.previewIcon.damDownloadedContent;
    const damContentSelectorImage = data?.component?.previewIcon.damContentSelector?.assetData?.[0];

    iconUrl = damDownloadedContentImage?.url || damContentSelectorImage?.selectedImageUrl || iconUrl;
  }

  const impressionProviderContextData = useImpression({
    data: {
      id: componentId,
      name: 'CapabilityCard',
      component: 'CapabilityCard',
      position: componentPosition,
      type: 'content'
    }
  });

  return (
    <div
      className="capability-card"
      data-component="CapabilityCard"
      ref={impressionProviderContextData.ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={impressionProviderContextData.clickID}
    >
      <Card>
        <CardContent disablePadding orientation={isHorizontal ? 'horizontal' : 'vertical'}>
          {hasImage && imageUrl !== 'String' && (
            <div className="sui-w-full">
              <CardMedia
                src={imageUrl}
                aspect="square"
                alt={`Image for ${headlineData}`}
                title={`Image for ${headlineData}`}
              />
            </div>
          )}
          <div className="sui-flex sui-flex-col sui-space-y-4 sui-w-full">
            <CardTitle
              leadingIcon={hasIcon && iconUrl !== 'String' && (
                <img
                  width={24}
                  height={24}
                  src={iconUrl}
                  alt="icon"
                  className="sui-w-6 sui-h-6 sui-object-contain"
                />
              )}
              header={headlineData}
            />
            {richTextData && (
              <div>
                <RichText content={richTextData} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

CapabilityCard.propTypes = {
  componentId: PropTypes.string,
  componentClass: PropTypes.string,
  isHorizontal: PropTypes.bool,
  richText: PropTypes.string,
  headline: PropTypes.string,
  componentPosition: PropTypes.number
};

CapabilityCard.defaultProps = {
  componentId: undefined,
  componentClass: 'CapabilityCard',
  componentPosition: 1,
  isHorizontal: false,
  richText: undefined,
  headline: undefined,
};

CapabilityCard.displayName = 'CapabilityCard';

CapabilityCard.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['CapabilityCard']).isRequired()
  }).shape({
    CapabilityCard: fragment().shape({
      id: stringType(),
      previewIcon: shape({
        damContentSelector: shape({
          assetData: arrayOf(shape({
            selectedImageUrl: stringType()
          }))
        }),
        damDownloadedContent: shape({
          url: stringType()
        })
      }),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(shape({
            selectedImageUrl: stringType()
          }))
        }),
        damDownloadedContent: shape({
          url: stringType()
        })
      }),
      headline: stringType(),
      richTextContent: stringType(),
    })
  })
};

export { CapabilityCard };
